import React, { useEffect, useState } from "react";
import { Container, Row, Col, Table, Button, Form, Card, Spinner, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";

export default function D406List() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [search, setSearch] = useState("");
  
  const [lunaInput, setLunaInput] = useState(new Date().getMonth() + 1);
  const [anInput, setAnInput] = useState(new Date().getFullYear());
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await authFetch(`/d406/?page=${page}&per_page=${perPage}&search=${search}`);
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Eroare la încărcarea declarațiilor D406: ${e?.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, search]);

  const handleCheckOrCreate = async (e) => {
    e.preventDefault();
    setChecking(true);
    try {
      const res = await authFetch("/d406/check-luna-an", {
        method: "POST",
        body: JSON.stringify({ bxluna: Number(lunaInput), bxan: Number(anInput) }),
      });
      if (res.result === "pass") {
        toast.success("Declarație D406 SAF-T deschisă cu succes!");
        navigate(`/d406/edit/${res.kyValue}`);
      } else {
        toast.error(res.message || "Eroare la verificarea declarației.");
      }
    } catch (err) {
      toast.error(`Eroare: ${err?.message || err}`);
    } finally {
      setChecking(false);
    }
  };

  const handleDelete = async (bxid) => {
    if (!window.confirm("Sigur doriți să ștergeți această declarație D406?")) return;
    try {
      await authFetch(`/d406/${bxid}`, { method: "DELETE" });
      toast.success("Declarație D406 ștersă cu succes!");
      load();
    } catch (err) {
      toast.error(`Eroare la ștergerea declarației: ${err?.message || err}`);
    }
  };

  const handleDownloadXml = async (bxid, an, luna, isActive = false) => {
    const url = isActive ? `/d406/${bxid}/xml-active` : `/d406/${bxid}/xml`;
    const label = isActive ? "SAF-T Active (Mijloace Fixe)" : "SAF-T Lunar";
    const filenamePrefix = isActive ? "D406Active" : "D406";
    
    try {
      toast.info(`Se generează fișierul ${label}...`);
      const blob = await authFetch(url, {
        responseType: "blob",
      });
      const file = new Blob([blob], { type: "application/xml" });
      const fileURL = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = fileURL;
      link.setAttribute("download", `${filenamePrefix}_${an}_${String(luna).padStart(2, "0")}.xml`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Descărcare ${label} inițiată!`);
    } catch (err) {
      toast.error(`Eroare la descărcarea XML: ${err?.message || err}`);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <Container className="mt-4">
      <h2 className="mb-4">D406 — Declarație SAF-T (Standard Audit File for Tax)</h2>
      
      <Row className="mb-4">
        <Col md={6}>
          <Card className="p-3 shadow-sm border-0 bg-light">
            <Form onSubmit={handleCheckOrCreate}>
              <h5 className="mb-3 text-secondary">Verifică / Adaugă Declarație SAF-T</h5>
              <Row className="align-items-end">
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>Luna</Form.Label>
                    <Form.Select value={lunaInput} onChange={(e) => setLunaInput(e.target.value)}>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col sm={4}>
                  <Form.Group>
                    <Form.Label>Anul</Form.Label>
                    <Form.Control
                      type="number"
                      value={anInput}
                      onChange={(e) => setAnInput(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col sm={4} className="mt-2 mt-sm-0">
                  <Button variant="primary" type="submit" className="w-100" disabled={checking}>
                    {checking ? <Spinner size="sm" /> : "Deschide"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
        
        <Col md={6} className="mt-3 mt-md-0 d-flex align-items-end justify-content-md-end">
          <InputGroup style={{ maxWidth: "350px" }}>
            <Form.Control
              placeholder="Căutare după an..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <Button variant="outline-secondary" onClick={() => { setSearch(""); setPage(1); }}>
              Reset
            </Button>
          </InputGroup>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="bg-dark text-white">
                <tr>
                  <th>An</th>
                  <th>Luna</th>
                  <th>CUI</th>
                  <th>Firma</th>
                  <th className="text-end">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-muted">
                      Nu s-au găsit declarații D406.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.bxid}>
                      <td className="align-middle fw-bold">{it.bxan}</td>
                      <td className="align-middle">{it.bxluna}</td>
                      <td className="align-middle">{it.cui}</td>
                      <td className="align-middle">{it.firma}</td>
                      <td className="align-middle text-end">
                        <Button
                          variant="info"
                          size="sm"
                          className="me-2 text-white"
                          onClick={() => navigate(`/d406/edit/${it.bxid}`)}
                        >
                          Editează
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          className="me-2"
                          onClick={() => handleDownloadXml(it.bxid, it.bxan, it.bxluna, false)}
                        >
                          XML General
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2 text-white"
                          onClick={() => handleDownloadXml(it.bxid, it.bxan, it.bxluna, true)}
                        >
                          XML Active
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(it.bxid)}
                        >
                          Șterge
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
          {totalPages > 1 && (
            <Card.Footer className="d-flex justify-content-between align-items-center bg-white border-0 py-3">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="text-muted">
                Pagina {page} din {totalPages}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Următor
              </Button>
            </Card.Footer>
          )}
        </Card>
      )}
    </Container>
  );
}
